import { inject, Injector } from '@angular/core'
import { HttpInterceptorFn, HttpRequest } from '@angular/common/http'
import { UserStore } from '../store/user/user.store'
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs'
import { setStorageItem } from '../common/utils/storage.utils'
import { UserService } from '../services/user.service'
import { Router } from '@angular/router'
import { MessageService } from 'primeng/api'
import { resolvePortalSubdomain } from '../common/utils/portal-subdomain.util'

let isRefreshing = false
const refreshTokenSubject = new BehaviorSubject<string | null>(null)
let lastForbiddenToastAt = 0
const FORBIDDEN_TOAST_COOLDOWN_MS = 3000

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const injector = inject(Injector)

  if (req.url.includes('/assets/')) {
    return next(req)
  }

  const userStore = inject(UserStore)
  const userService = inject(UserService)

  const token = userStore.getToken()
  const refreshToken = userStore.getRefreshToken()

  const addAuthHeaders = (request: HttpRequest<any>, accessToken: string, csrf?: string | null) => {
    const headers: Record<string, string> = { Authorization: `Bearer ${accessToken}` }
    const csrfValue = csrf ?? userStore.getCsrfToken()
    if (csrfValue && !['GET', 'HEAD', 'OPTIONS'].includes(request.method.toUpperCase())) {
      headers['X-CSRF-Token'] = csrfValue
    }
    return request.clone({ setHeaders: headers })
  }

  const withTenantHeader = (request: HttpRequest<any>) => {
    const subdomain = resolvePortalSubdomain()
    if (!subdomain) return request
    return request.clone({ setHeaders: { 'X-Subdomain': subdomain } })
  }

  const handle401 = () => {
    const router = injector.get(Router)

    if (req.url.includes('/auth/refresh')) {
      isRefreshing = false
      userStore.logout()
      void router.navigateByUrl('/login')
      return throwError(() => new Error('Session expired'))
    }

    if (!refreshToken) {
      userStore.logout()
      void router.navigateByUrl('/login')
      return throwError(() => new Error('No refresh token'))
    }

    if (!isRefreshing) {
      isRefreshing = true
      refreshTokenSubject.next(null)

      return userService.refreshToken(refreshToken).pipe(
        switchMap((resp) => {
          isRefreshing = false
          const newToken = (resp as any).accessToken ?? (resp as any).sessionToken
          const newRefresh = resp.refreshToken
          const newCsrf = (resp as any).csrfToken

          userStore.setToken(newToken)
          userStore.setRefreshToken(newRefresh ?? refreshToken)
          if (newCsrf) {
            userStore.setCsrfToken(newCsrf)
            setStorageItem('auth_csrf', newCsrf)
          }
          refreshTokenSubject.next(newToken)

          return next(withTenantHeader(addAuthHeaders(req, newToken, newCsrf)))
        }),
        catchError((refreshError) => {
          isRefreshing = false
          userStore.logout()
          void router.navigateByUrl('/login')
          return throwError(() => refreshError)
        }),
      )
    }

    return refreshTokenSubject.pipe(
      filter((t) => t !== null),
      take(1),
      switchMap((newToken) => next(withTenantHeader(addAuthHeaders(req, newToken!)))),
    )
  }

  const authReq = withTenantHeader(token ? addAuthHeaders(req, token) : req)

  return next(authReq).pipe(
    catchError((error) => {
      if (error.status === 401) {
        // Login/recovery 401 must surface to the form — never attempt token refresh.
        if (
          req.url.includes('/auth/login') ||
          req.url.includes('/auth/password-recovery') ||
          req.url.includes('/auth/mfa/verify')
        ) {
          return throwError(() => error)
        }
        return handle401()
      }
      if (error.status === 403 && !req.url.includes('/auth/')) {
        const messageService = injector.get(MessageService, null)
        const now = Date.now()
        if (messageService && now - lastForbiddenToastAt >= FORBIDDEN_TOAST_COOLDOWN_MS) {
          lastForbiddenToastAt = now
          messageService.add({
            severity: 'warn',
            summary: 'Sin permiso',
            detail: 'No tienes permiso para realizar esta acción.',
            life: 4500,
          })
        }
      }
      return throwError(() => error)
    }),
  )
}
